/**
 * Order tracking display.
 *
 * Promise Time: the ETA is the server-owned `order.promise.current`, rendered
 * as a window ("19:40 to 19:55"). `legacyEta` is the ONE isolated fallback,
 * used only when an order has no promise (placed before Phase 0) or the
 * branch's shadow switch is off. Delete it in Phase 1. It never invents
 * minutes: with no committed estimate and no branch configuration it says
 * the time is to be confirmed.
 */
import { OrderStatusEnum, OrderTypeEnum } from "@/api/online-ordering-service/model";
import type { OrderOutput, OrderPromise } from "@/api/online-ordering-service/model";
import type { ActiveBranch } from "@/store/branch";
import { formatWindow, publicWindow, visiblePromise, type TFn } from "@/lib/promise";

export interface OrderEtaResult {
  headline: string;
  primaryTime: string;
  unit: string | null;
  secondaryText: string | null;
  isReadyOrDelivered: boolean;
  isOverdue: boolean;
  remainingMinutes: number;
  /** Where the time came from: the promise, the legacy fallback, or nothing known. */
  source: "promise" | "legacy" | "none";
}

export interface ComputeEtaOptions {
  now?: Date;
  locale?: string;
  /** BranchConfig.visibleToCustomers for the order's branch. */
  promiseVisible?: boolean | null;
}

export function stageFromStatus(status: OrderStatusEnum | undefined | null): number {
  if (!status) return 0;
  if (status === OrderStatusEnum.Delivered || status === OrderStatusEnum.Completed) return 3;
  if (status === OrderStatusEnum.OutForDelivery || status === OrderStatusEnum.ReadyForPickup) return 2;
  if (status === OrderStatusEnum.Preparing) return 1;
  return 0;
}

export function formatTime(date: Date, locale?: string): string {
  try {
    return date.toLocaleTimeString(
      locale || (typeof navigator !== "undefined" ? navigator.language : "en-US"),
      { hour: "numeric", minute: "2-digit" },
    );
  } catch {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
}

function headlineFor(isPickup: boolean, t: TFn): string {
  return isPickup
    ? t("v2.tracking.estimated_ready", "Estimated ready time")
    : t("v2.tracking.estimated_arrival", "Estimated arrival");
}

function unknownResult(isPickup: boolean, t: TFn): OrderEtaResult {
  return {
    headline: headlineFor(isPickup, t),
    primaryTime: t("v2.tracking.time_pending", "Time to be confirmed"),
    unit: null,
    secondaryText: null,
    isReadyOrDelivered: false,
    isOverdue: false,
    remainingMinutes: 0,
    source: "none",
  };
}

function terminalResult(order: OrderOutput, t: TFn): OrderEtaResult | null {
  const isPickup = order.orderType === OrderTypeEnum.Pickup;
  const stage = stageFromStatus(order.status);
  if (stage === 3) {
    const label = isPickup ? t("v2.tracking.picked_up", "Picked up") : t("v2.tracking.delivered", "Delivered");
    return {
      headline: label, primaryTime: label, unit: null, secondaryText: null,
      isReadyOrDelivered: true, isOverdue: false, remainingMinutes: 0, source: order.promise ? "promise" : "legacy",
    };
  }
  if (isPickup && stage === 2) {
    return {
      headline: t("v2.tracking.ready_for_pickup", "Ready for pickup"),
      primaryTime: t("v2.tracking.ready_now", "Ready now"),
      unit: null,
      secondaryText: t("v2.tracking.stages.ready_for_pickup.note", "Ready to collect"),
      isReadyOrDelivered: true, isOverdue: false, remainingMinutes: 0, source: order.promise ? "promise" : "legacy",
    };
  }
  return null;
}

function promiseEta(order: OrderOutput, promise: OrderPromise, t: TFn, options: ComputeEtaOptions): OrderEtaResult {
  const now = options.now ?? new Date();
  const isPickup = promise.kind !== "delivery";
  const stage = stageFromStatus(order.status);
  const window = publicWindow(promise);
  const f = formatWindow(window, options.locale) ?? { from: "", to: "" };
  const latestMs = new Date(window.latest).getTime();
  const isOverdue = now.getTime() > latestMs;

  let secondaryText: string | null = null;
  if (promise.customerNotifiedLateAt) {
    secondaryText = isPickup
      ? t("v2.tracking.late_ready", {
          from: f.from, to: f.to,
          defaultValue: `Your order is taking longer than expected. Updated ready time: ${f.from} to ${f.to}.`,
        })
      : t("v2.tracking.late_arrival", {
          from: f.from, to: f.to,
          defaultValue: `Your order is taking longer than expected. Updated arrival: ${f.from} to ${f.to}.`,
        });
  } else if (isOverdue) {
    secondaryText = t("v2.tracking.almost_there", "Almost there");
  } else if (!isPickup && stage === 2) {
    secondaryText = t("v2.tracking.on_its_way", "On its way");
  } else if (stage === 1) {
    secondaryText = t("v2.tracking.kitchen_started", "Kitchen started");
  } else if (promise.current.source === "scheduled") {
    secondaryText = t("v2.tracking.scheduled_order", "Scheduled order");
  }

  return {
    headline: headlineFor(isPickup, t),
    primaryTime: t("v2.tracking.window", { from: f.from, to: f.to, defaultValue: `${f.from} to ${f.to}` }),
    unit: null,
    secondaryText,
    isReadyOrDelivered: false,
    isOverdue,
    remainingMinutes: Math.max(0, Math.ceil((latestMs - now.getTime()) / 60_000)),
    source: "promise",
  };
}

function positive(value: unknown): number | null {
  return typeof value === "number" && value > 0 ? value : null;
}

export interface LegacyMinLeadOptions {
  isDineIn: boolean;
  mode: "delivery" | "pickup" | string;
}

/**
 * Pre-Phase-0 scheduled-lead floor (kitchen + delivery + 15), kept only as
 * checkout's fallback for the minimum scheduled-time floor when the quote
 * has not answered yet, failed, or omitted `minimumLeadMinutes`. Delete
 * alongside `legacyEta` in Phase 1. Never invented as a display value, only
 * as a submission-blocking floor, so it is fine for this one to keep its
 * pre-Phase-0 defaults (15 / 30) unlike `legacyEta`'s display path.
 */
export function legacyMinLeadMinutes(
  branch: ActiveBranch | null | undefined,
  opts: LegacyMinLeadOptions,
): number {
  const b = branch as
    | { estimatedKitchenPreparationMinutes?: number | null; estimatedDeliveryDurationMinutes?: number | null }
    | null
    | undefined;
  const kitchenMins = b?.estimatedKitchenPreparationMinutes ?? 15;
  const deliveryMins = !opts.isDineIn && opts.mode === "delivery" ? (b?.estimatedDeliveryDurationMinutes ?? 30) : 0;
  return kitchenMins + deliveryMins + 15;
}

/**
 * The scheduled-time floor checkout actually uses: only a successful,
 * settled quote's `minimumLeadMinutes` overrides `legacyMinLeadMinutes`. A
 * quote that is loading, failed, or missing the field must never fail open
 * to 0 (there is no server-side enforcement of the lead on order create).
 */
export function resolveMinLeadMinutes(
  quote: { minimumLeadMinutes?: number | null } | null | undefined,
  loading: boolean,
  error: unknown,
  legacyMinLead: number,
): number {
  if (!loading && !error && typeof quote?.minimumLeadMinutes === "number") {
    return quote.minimumLeadMinutes;
  }
  return legacyMinLead;
}

/**
 * Pre-Phase-0 display, kept only for orders without a promise and for
 * branches in shadow mode. Reads the order's committed estimate first, then
 * branch configuration. Never falls back to invented minutes.
 */
export function legacyEta(
  order: OrderOutput,
  branch: ActiveBranch | null | undefined,
  t: TFn,
  options: ComputeEtaOptions = {},
): OrderEtaResult {
  const terminal = terminalResult(order, t);
  if (terminal) return { ...terminal, source: "legacy" };

  const isPickup = order.orderType === OrderTypeEnum.Pickup;
  const stage = stageFromStatus(order.status);
  const now = options.now ?? new Date();
  const locale = options.locale;
  const b = branch as
    | { estimatedKitchenPreparationMinutes?: number | null; estimatedDeliveryDurationMinutes?: number | null }
    | null
    | undefined;
  const kitchenMins = positive(b?.estimatedKitchenPreparationMinutes);
  const transitMins = isPickup ? 0 : positive(b?.estimatedDeliveryDurationMinutes) ?? 0;

  let totalDuration: number | null = null;
  if (!isPickup && positive(order.estimatedDeliveryMinutes) != null) {
    totalDuration = order.estimatedDeliveryMinutes as number;
  } else if (isPickup && positive(order.estimatedPreparationMinutes) != null) {
    totalDuration = order.estimatedPreparationMinutes as number;
  } else if (kitchenMins != null && (isPickup || transitMins > 0)) {
    totalDuration = kitchenMins + transitMins;
  }
  if (totalDuration == null) return unknownResult(isPickup, t);

  const headline = headlineFor(isPickup, t);
  const base = { unit: null, isReadyOrDelivered: false, source: "legacy" as const };

  if (order.scheduledTimestamp) {
    const scheduledDate = new Date(order.scheduledTimestamp);
    const scheduledMs = scheduledDate.getTime();
    if (!Number.isNaN(scheduledMs)) {
      const prepStartMs = scheduledMs - totalDuration * 60_000;
      const scheduledLabel = formatTime(scheduledDate, locale);
      if (now.getTime() < prepStartMs) {
        const prepLabel = formatTime(new Date(prepStartMs), locale);
        return {
          ...base,
          headline: t("v2.tracking.scheduled_order", "Scheduled order"),
          primaryTime: scheduledLabel,
          secondaryText: t("v2.tracking.prep_starts_at", { time: prepLabel, defaultValue: `Prep starts around ${prepLabel}` }),
          isOverdue: false,
          remainingMinutes: Math.max(1, Math.ceil((scheduledMs - now.getTime()) / 60_000)),
        };
      }
      if (now.getTime() <= scheduledMs) {
        const remaining = Math.max(1, Math.ceil((scheduledMs - now.getTime()) / 60_000));
        return {
          ...base,
          headline,
          primaryTime: String(remaining),
          unit: t("v2.tracking.min", "min"),
          secondaryText: isPickup
            ? t("v2.tracking.ready_by", { time: scheduledLabel, defaultValue: `Ready by ${scheduledLabel}` })
            : t("v2.tracking.arriving_by", { time: scheduledLabel, defaultValue: `Arriving by ${scheduledLabel}` }),
          isOverdue: false,
          remainingMinutes: remaining,
        };
      }
      return {
        ...base,
        headline,
        primaryTime: t("v2.tracking.any_moment", "Any moment now"),
        secondaryText: t("v2.tracking.almost_there", "Almost there"),
        isOverdue: true,
        remainingMinutes: 1,
      };
    }
  }

  const createdMs = order.createdAt ? new Date(order.createdAt).getTime() : now.getTime();
  const elapsedMins = Math.floor(Math.max(0, now.getTime() - createdMs) / 60_000);
  let remainingMins: number;
  if (isPickup) {
    remainingMins = totalDuration - elapsedMins;
  } else if (stage >= 2) {
    const transitRemaining = totalDuration - elapsedMins;
    remainingMins = transitMins > 0 ? Math.min(transitMins, Math.max(2, transitRemaining)) : Math.max(2, transitRemaining);
  } else if (stage === 1) {
    remainingMins = Math.max(transitMins + 2, totalDuration - elapsedMins);
  } else {
    remainingMins = Math.max(1, totalDuration - elapsedMins);
  }

  if (elapsedMins >= totalDuration || remainingMins <= 0) {
    return {
      ...base,
      headline,
      primaryTime: t("v2.tracking.any_moment", "Any moment now"),
      secondaryText: t("v2.tracking.almost_there", "Almost there"),
      isOverdue: true,
      remainingMinutes: 1,
    };
  }

  const target = formatTime(new Date(createdMs + totalDuration * 60_000), locale);
  const safeRemaining = Math.max(1, remainingMins);
  return {
    ...base,
    headline,
    primaryTime: String(safeRemaining),
    unit: t("v2.tracking.min", "min"),
    secondaryText: isPickup
      ? t("v2.tracking.ready_by", { time: target, defaultValue: `Ready by ${target}` })
      : t("v2.tracking.arriving_by", { time: target, defaultValue: `Arriving by ${target}` }),
    isOverdue: false,
    remainingMinutes: safeRemaining,
  };
}

export function computeOrderEta(
  order: OrderOutput | null | undefined,
  branch: ActiveBranch | null | undefined,
  t: TFn,
  options: ComputeEtaOptions = {},
): OrderEtaResult {
  if (!order) return unknownResult(false, t);
  const promise = visiblePromise(order, options.promiseVisible);
  const terminal = terminalResult(order, t);
  if (terminal) return terminal;
  if (promise) return promiseEta(order, promise, t, options);
  return legacyEta(order, branch, t, options);
}
